import { z } from 'zod';
import type { SchemaInput } from './schema-input.js';
import { savedRouteOptionSchema } from './saved-route.js';

const pair = z.tuple([z.number().finite(), z.number().finite()]);
export const measurementCameraSchema = z.object({ p: z.number().min(0).max(60), b: z.number().finite(), t: z.boolean().optional() });
const coordinate = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
const distanceRouteOption = savedRouteOptionSchema.omit({ coords: true }).extend({
  legCoords: z.array(z.array(coordinate).max(100_000)).max(499),
  legGapCoords: z.array(z.array(coordinate).max(100_000).nullable()).max(499),
  allLegsRouted: z.boolean(),
});
export const measurementStateSchema = z.object({
  c: pair,
  z: z.number().min(0).max(24),
  r: z.number().finite().optional(),
  v: measurementCameraSchema.optional(),
  pts: z.array(pair).min(1).max(500),
  cl: z.number().optional(),
  b: z.string().optional(),
  rm: z.enum(['driving', 'walking', 'cycling']).optional(),
  um: z.boolean().optional(),
  da: z.number().int().min(0).max(2).optional(),
  sg: z.array(z.union([z.tuple([z.number().int()]), z.tuple([z.number().int(), z.string()])])).optional(),
  s0: z.string().optional(),
  addresses: z.array(z.string().max(1000)).max(500).optional(),
  routes: z.array(savedRouteOptionSchema).max(3).optional(),
  distanceRoutes: z.array(distanceRouteOption).max(3).optional(),
});

export const savedMeasurementSchema = z.object({
  app: z.enum(['distance', 'route-planner']),
  state: measurementStateSchema,
}).refine(({ state }) => !state.addresses || state.addresses.length === state.pts.length)
  .refine(({ state }) => [...(state.routes ?? []), ...(state.distanceRoutes ?? [])].every(route => route.legDistancesKm.length === state.pts.length - 1));

export type SavedMeasurement = z.infer<typeof savedMeasurementSchema>;

export function readSavedMeasurement(value: SchemaInput) {
  const result = z.object({ measurement: savedMeasurementSchema }).safeParse(value);
  return result.success ? result.data.measurement : null;
}

export function measurementPath(app: 'distance' | 'route-planner'): '/distance-between-two-addresses/' | '/route-planner/' {
  return app === 'distance' ? '/distance-between-two-addresses/' : '/route-planner/';
}
